import React, { useState, useEffect, useRef } from "react";
import { X, ArrowRight, Check, Sparkles, Loader2, Upload, FileText, SkipForward } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { DoxMascot } from "@/components/Logo";

/** Convert extracted_fields dict → labeled tiles, skipping empty values. */
function formatFields(fields) {
  if (!fields) return [];
  const LABELS = {
    license_number: "License #",
    permit_number: "Permit #",
    insurance_policy_number: "Policy #",
    bond_number: "Bond #",
    tax_id: "Tax ID",
    ein: "EIN",
    business_name: "Business",
    expiration_date: "Expires",
    issue_date: "Issued",
    effective_date: "Effective",
    renewal_date: "Renews",
    holder_name: "Holder",
    agency: "Agency",
    state: "State",
  };
  const out = [];
  for (const [k, v] of Object.entries(fields)) {
    if (!v) continue;
    if (Array.isArray(v)) continue;
    const s = String(v).trim();
    if (!s) continue;
    out.push({ key: k, label: LABELS[k] || k.replace(/_/g, " "), value: s });
    if (out.length >= 6) break;
  }
  return out;
}

/**
 * 60-second first-run onboarding — hand Dox states + trades so the platform
 * can present renewals + recommendations immediately, taking the user to their
 * first "YES — Renew it for me" moment.
 *
 * Steps: (1) states → (2) trades → (3) optional first document.
 * Profile is saved at the moment the user leaves step 2 so the third step is
 * purely optional — skipping it still counts as a successful onboarding.
 */
export default function OnboardingWizard({ business, onClose, onComplete }) {
  const [step, setStep] = useState(0);
  const [celebrating, setCelebrating] = useState(false);
  const [states, setStates] = useState([]);
  // Prefill the paperwork-pain answer from the DoxWidget scripted flow so
  // returning visitors aren't asked the same question twice. Falls back to
  // empty string for direct signups.
  const [pain, setPain] = useState(() => {
    try {
      const raw = sessionStorage.getItem("dox-widget-state-v1");
      return (raw && JSON.parse(raw)?.answers?.pain) || "";
    } catch { return ""; }
  });
  const [trades, setTrades] = useState([]);
  const [tradeCategories, setTradeCategories] = useState([]);
  const [tradeSearch, setTradeSearch] = useState("");
  const [selStates, setSelStates] = useState(business?.states || []);
  const [selTrades, setSelTrades] = useState(business?.trades || []);
  const [savingProfile, setSavingProfile] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState(null);
  const [uploadedDocId, setUploadedDocId] = useState(null);
  const [uploadedPreview, setUploadedPreview] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [analysisState, setAnalysisState] = useState("idle");
  const fileInputRef = useRef(null);
  const pollTimer = useRef(null);

  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const [s, t] = await Promise.all([
          api.get("/compliance/states"),
          api.get("/compliance/trades"),
        ]);
        if (!cancel) {
          setStates(s.data.items || []);
          setTrades(t.data.items || []);
          setTradeCategories(t.data.categories || []);
        }
      } catch {
        toast.error("Couldn't load onboarding data.");
      }
    })();
    return () => { cancel = true; };
  }, []);

  const canNext = step === 0 ? selStates.length > 0 : step === 1 ? selTrades.length > 0 : true;

  const toggle = (arr, setArr) => (v) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

  // Save the profile once when leaving step 1 into the optional upload step.
  const saveProfileAndAdvance = async () => {
    setSavingProfile(true);
    try {
      await api.put("/compliance/business/profile", { states: selStates, trades: selTrades });
      try { await api.post("/compliance/schedule-renewals", {}); } catch { /* non-blocking */ }
      setStep(2);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save your profile.");
    } finally {
      setSavingProfile(false);
    }
  };

  const uploadFirstDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    // Build a preview for image types
    if (file.type?.startsWith("image/")) {
      try {
        const url = URL.createObjectURL(file);
        setUploadedPreview({ kind: "image", url });
      } catch { /* ignore */ }
    } else {
      setUploadedPreview({ kind: "file", url: null });
    }
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await api.post("/documents/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadedName(file.name);
      setUploadedDocId(data?.id || data?.document_id || data?.doc_id);
      setAnalysisState("reading");
      pollForAnalysis(data?.id || data?.document_id || data?.doc_id);
      return data;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed — you can try again from the Documents tab.");
      setUploadedPreview(null);
    } finally {
      setUploading(false);
    }
  };

  const pollForAnalysis = (docId) => {
    if (!docId) return;
    let attempts = 0;
    const MAX = 15; // ~30s total at 2s interval
    const tick = async () => {
      attempts += 1;
      try {
        const { data } = await api.get(`/documents/${docId}`);
        if (data?.status === "analyzed") {
          setAnalysis(data);
          setAnalysisState("done");
          return;
        }
      } catch { /* ignore transient */ }
      if (attempts >= MAX) {
        setAnalysisState("timeout");
        return;
      }
      pollTimer.current = setTimeout(tick, 2000);
    };
    pollTimer.current = setTimeout(tick, 1200);
  };

  const reanalyze = async () => {
    if (!uploadedDocId) return;
    setAnalysisState("reading");
    setAnalysis(null);
    try {
      await api.post(`/documents/${uploadedDocId}/reanalyze`);
      pollForAnalysis(uploadedDocId);
      toast.message("Taking another look…");
    } catch (e) {
      setAnalysisState("done");
      toast.error(e?.response?.data?.detail || "Couldn't re-run the extractor.");
    }
  };

  useEffect(() => () => { if (pollTimer.current) clearTimeout(pollTimer.current); }, []);

  const finish = async () => {
    // Mark this session as "just finished onboarding" so the Dashboard plays
    // the first-run cinematic (specialists coming online) on next render.
    try { localStorage.setItem("dox-first-run", "1"); } catch { /* no-op */ }
    // Show the celebration card first, then hand off to onComplete after a short beat.
    setCelebrating(true);
  };

  const closeCelebration = () => {
    onComplete && onComplete();
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-[80] flex items-center justify-center p-4 sm:p-6"
      data-testid="onboarding-wizard"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-md" onClick={celebrating ? closeCelebration : onClose} />
      {celebrating ? (
        <CompletionCard
          firstUploadName={uploadedName}
          hasStates={selStates.length}
          hasTrades={selTrades.length}
          onGo={closeCelebration}
        />
      ) : (
      <div className="relative w-full max-w-2xl rounded-3xl glass-strong border border-white/10 overflow-hidden">
        {/* Header */}
        <div className="flex items-start gap-4 px-6 sm:px-8 pt-6 sm:pt-7 pb-4">
          <div className="animate-float shrink-0"><DoxMascot size={56} emotion={step === 3 ? "reading" : "greeting"} /></div>
          <div className="flex-1">
            <div className="text-xs tracking-[0.2em] uppercase font-bold text-dox-orange">
              {step === 0 ? "Welcome" : step === 3 ? "One last thing" : "A few quick questions"}
            </div>
            <h2 className="font-display font-black text-2xl sm:text-3xl mt-1">
              {step === 0 && "Hi, I'm Dox. Let's get your business compliant."}
              {step === 1 && "Which trades do you run?"}
              {step === 2 && "What's your biggest paperwork headache?"}
              {step === 3 && "Hand me one document — optional."}
            </h2>
            <p className="text-white/60 text-sm mt-1">
              {step === 0 && "Just two clicks: tell me where you operate and which trades you run. I'll pull every license, renewal, and deadline for you — with a one-tap YES on each."}
              {step === 1 && "Pick every trade or line of business you run. I cover every licensed trade in America."}
              {step === 2 && "Pick whichever feels most painful today. I'll start there so you feel results the first hour."}
              {step === 3 && "Give me a license, insurance certificate, or bond and I'll read it, file it, and track its next renewal. Or skip — you can add documents anytime."}
            </p>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white p-2 -m-2 min-h-[44px] min-w-[44px]" data-testid="onboarding-close" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Step indicator */}
        <div className="px-6 sm:px-8 pb-3 flex items-center gap-2 text-xs text-white/50">
          <StepDot on={step >= 0} label="States" />
          <div className="h-px flex-1 bg-white/10" />
          <StepDot on={step >= 1} label="Trades" />
          <div className="h-px flex-1 bg-white/10" />
          <StepDot on={step >= 2} label="Focus" />
          <div className="h-px flex-1 bg-white/10" />
          <StepDot on={step >= 3} label="First doc" muted />
        </div>

        {/* Body */}
        <div className="px-6 sm:px-8 pb-4 max-h-[52vh] overflow-y-auto">
          {step === 0 && (
            <div>
              <div className="text-white/80 font-semibold mb-3 text-sm">Which states do you operate in?</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="states-grid">
                {states.map((s) => {
                  const active = selStates.includes(s.abbrev);
                  return (
                    <button
                      key={s.abbrev}
                      onClick={() => toggle(selStates, setSelStates)(s.abbrev)}
                      className={`text-left rounded-xl border px-3 py-2.5 min-h-[48px] text-sm transition-colors ${
                        active ? "bg-dox-orange/15 border-dox-orange/50 text-dox-orange" : "border-white/10 hover:border-white/25 text-white/75"
                      }`}
                      data-testid={`state-${s.abbrev}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium truncate">{s.name}</span>
                        {active && <Check size={14} className="shrink-0" />}
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          {step === 1 && (
            <div data-testid="trades-step">
              <div className="text-white/80 font-semibold mb-3 text-sm">
                Which trades or lines of business apply?
              </div>

              <div className="relative mb-3">
                <input
                  type="search"
                  value={tradeSearch}
                  onChange={(e) => setTradeSearch(e.target.value)}
                  placeholder={`Search ${trades.length}+ trades…`}
                  data-testid="trades-search"
                  aria-label="Search trades"
                  className="w-full bg-white/5 border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/40 outline-none focus:border-dox-orange focus:ring-2 focus:ring-dox-orange/20 transition text-sm"
                />
                {tradeSearch && (
                  <button
                    onClick={() => setTradeSearch("")}
                    aria-label="Clear search"
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40 hover:text-white/80 text-xs"
                  >
                    clear
                  </button>
                )}
              </div>

              {/* Show selected chips first — no matter how deep in the catalog. */}
              {selTrades.length > 0 && (
                <div className="mb-4 flex flex-wrap gap-1.5" data-testid="trades-selected">
                  {selTrades.map((slug) => {
                    const t = trades.find((x) => x.slug === slug);
                    return (
                      <button
                        key={slug}
                        onClick={() => toggle(selTrades, setSelTrades)(slug)}
                        className="inline-flex items-center gap-1 rounded-full bg-dox-orange/15 border border-dox-orange/40 text-dox-orange px-3 py-1 text-xs font-semibold hover:bg-dox-orange/25 transition-colors"
                      >
                        <Check size={11} /> {t?.name || slug}
                      </button>
                    );
                  })}
                </div>
              )}

              <div
                className="max-h-[340px] overflow-y-auto pr-1 -mr-1 space-y-4 scrollbar-thin"
                data-testid="trades-grid"
              >
                {(() => {
                  const q = tradeSearch.trim().toLowerCase();
                  const cats = tradeCategories.length ? tradeCategories : [{ slug: "all", label: "Trades", items: trades }];
                  return cats
                    .map((cat) => {
                      const items = (cat.items || []).filter(
                        (t) => !q || t.name.toLowerCase().includes(q) || t.slug.includes(q)
                      );
                      if (items.length === 0) return null;
                      return (
                        <div key={cat.slug} data-testid={`trades-category-${cat.slug}`}>
                          <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">
                            {cat.label} · {items.length}
                          </div>
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {items.map((t) => {
                              const active = selTrades.includes(t.slug);
                              return (
                                <button
                                  key={t.slug}
                                  onClick={() => toggle(selTrades, setSelTrades)(t.slug)}
                                  className={`text-left rounded-xl border px-3.5 py-3 min-h-[48px] text-sm transition-colors ${
                                    active
                                      ? "bg-dox-orange/15 border-dox-orange/50 text-dox-orange"
                                      : "border-white/10 hover:border-white/25 text-white/75"
                                  }`}
                                  data-testid={`trade-${t.slug}`}
                                >
                                  <div className="flex items-center justify-between gap-2">
                                    <span className="font-medium truncate">{t.name}</span>
                                    {active && <Check size={14} className="shrink-0" />}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })
                    .filter(Boolean);
                })()}

                {/* No results state */}
                {tradeSearch.trim() &&
                  tradeCategories.every((cat) =>
                    (cat.items || []).every(
                      (t) => !t.name.toLowerCase().includes(tradeSearch.trim().toLowerCase()) && !t.slug.includes(tradeSearch.trim().toLowerCase())
                    )
                  ) && (
                    <div className="text-center py-6 text-sm text-white/50">
                      No match for &ldquo;{tradeSearch}&rdquo;. Try a different keyword — Dox covers every licensed trade in America.
                    </div>
                  )}
              </div>
            </div>
          )}
          {step === 2 && (
            <div data-testid="pain-step">
              <div className="text-white/80 font-semibold mb-3 text-sm">
                Pick your biggest paperwork headache (optional — it just tells my team where to start).
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="pain-grid">
                {[
                  { key: "renewals", label: "Renewals", note: "License, insurance, bond renewals" },
                  { key: "insurance certificates", label: "Insurance certificates", note: "COIs, GL policies, workers' comp" },
                  { key: "deadlines", label: "Deadlines & filings", note: "Never missing a date again" },
                  { key: "filing paperwork", label: "Filing paperwork", note: "Assembling and submitting packets" },
                ].map((p) => {
                  const active = pain === p.key;
                  return (
                    <button
                      key={p.key}
                      onClick={() => setPain(active ? "" : p.key)}
                      data-testid={`pain-${p.key.replace(/\s+/g, "-")}`}
                      className={`text-left rounded-xl border px-3.5 py-3 min-h-[64px] transition-colors ${
                        active
                          ? "bg-dox-orange/15 border-dox-orange/50 text-dox-orange"
                          : "border-white/10 hover:border-white/25 text-white/75"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <span className="font-semibold text-sm">{p.label}</span>
                        {active && <Check size={14} className="shrink-0" />}
                      </div>
                      <div className={`text-[11px] ${active ? "text-dox-orange/80" : "text-white/45"}`}>{p.note}</div>
                    </button>
                  );
                })}
              </div>
              <div className="text-[11px] text-white/40 mt-3">
                No wrong answer — I&apos;ll keep watching every category regardless of what you pick.
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="py-2">
              <input
                ref={fileInputRef}
                type="file"
                data-testid="onboarding-file-input"
                className="hidden"
                accept=".pdf,.png,.jpg,.jpeg,.docx,.txt"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadFirstDoc(f); e.target.value = ""; }}
              />
              {uploadedName ? (
                <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/10 p-5" data-testid="onboarding-upload-done">
                  <div className="flex gap-4 items-start">
                    {/* Thumbnail / file icon */}
                    <div className="shrink-0 h-24 w-20 rounded-xl bg-white/5 border border-white/10 overflow-hidden flex items-center justify-center">
                      {uploadedPreview?.kind === "image" && uploadedPreview.url ? (
                        <img src={uploadedPreview.url} alt="" className="h-full w-full object-cover" />
                      ) : (
                        <FileText size={26} className="text-white/60" />
                      )}
                    </div>
                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <div className="text-xs tracking-[0.2em] uppercase font-bold text-emerald-300 mb-1">Dox is on it</div>
                      <div className="font-semibold text-white truncate">{uploadedName}</div>

                      {analysisState === "reading" && (
                        <div className="mt-3 rounded-xl bg-white/5 border border-white/10 px-3 py-2.5 text-sm flex items-center gap-2 text-white/80" data-testid="analysis-reading">
                          <Loader2 size={14} className="animate-spin text-dox-orange" />
                          <span>I&apos;m reading it — pulling out license numbers, dates, and jurisdictions…</span>
                        </div>
                      )}

                      {analysisState === "done" && analysis && (
                        <div className="mt-3 space-y-2" data-testid="analysis-done">
                          <div className="flex items-start justify-between gap-3">
                            <div className="text-sm text-white/85">
                              <span className="font-semibold text-white">I found this:</span> {analysis.document_type || "a compliance document"}
                              {analysis.jurisdiction ? <> · <span className="text-emerald-300">{analysis.jurisdiction}</span></> : null}
                            </div>
                            <button
                              onClick={reanalyze}
                              disabled={analysisState === "reading"}
                              className="shrink-0 rounded-full border border-white/15 hover:border-dox-orange/60 text-white/70 hover:text-white px-3 py-1 text-[11px] font-semibold inline-flex items-center gap-1.5 disabled:opacity-40"
                              data-testid="analysis-reanalyze"
                            >
                              <Loader2 size={11} className={analysisState === "reading" ? "animate-spin" : ""} /> Try again
                            </button>
                          </div>
                          <div className="text-[11px] text-white/50">Tap any chip to correct it, or hit &ldquo;Try again&rdquo; for a fresh read.</div>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                            {formatFields(analysis?.extracted_fields).map((f) => (
                              <EditableChip
                                key={f.key}
                                fieldKey={f.key}
                                label={f.label}
                                value={f.value}
                                docId={uploadedDocId}
                                onSaved={(newVal) => {
                                  setAnalysis((prev) => prev ? { ...prev, extracted_fields: { ...(prev.extracted_fields || {}), [f.key]: newVal } } : prev);
                                }}
                              />
                            ))}
                          </div>
                          <div className="text-xs text-white/60 mt-2">
                            I&apos;ll track its renewal from here. Hit &ldquo;Let Dox do it&rdquo; to see it in the Command Center.
                          </div>
                        </div>
                      )}

                      {analysisState === "timeout" && (
                        <div className="mt-3 text-xs text-white/60">
                          Still reading… I&apos;ll finish this in the background — check Documents in a minute.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  data-testid="onboarding-upload-btn"
                  className="w-full rounded-2xl border-2 border-dashed border-white/15 hover:border-dox-orange/50 hover:bg-white/[0.03] px-6 py-10 text-center transition-colors disabled:opacity-60"
                >
                  {uploading ? (
                    <div className="flex flex-col items-center gap-2 text-white/70">
                      <Loader2 size={22} className="animate-spin" />
                      <div className="text-sm">Reading your document…</div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <Upload size={22} className="text-white/70" />
                      <div className="font-semibold text-white">Drop your first license or insurance certificate</div>
                      <div className="text-xs text-white/50">PDF, PNG, JPG, DOCX or TXT — I&apos;ll figure out the rest.</div>
                    </div>
                  )}
                </button>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 sm:px-8 pb-6 sm:pb-7 pt-2 flex items-center justify-between gap-3 border-t border-white/10 mt-2">
          <button
            onClick={onClose}
            className="text-sm text-white/45 hover:text-white/70 min-h-[44px] px-2"
            data-testid="onboarding-skip-all"
          >
            I&apos;ll set up later
          </button>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button
                onClick={() => setStep((s) => s - 1)}
                className="rounded-full border border-white/15 hover:border-white/30 px-5 py-2.5 min-h-[44px] text-sm text-white/80"
                data-testid="onboarding-back"
              >
                Back
              </button>
            )}
            {step === 0 && (
              <button
                onClick={() => setStep(1)}
                disabled={!canNext}
                className="rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-bold px-6 py-2.5 min-h-[48px] text-sm disabled:opacity-40 inline-flex items-center gap-2 transition-transform hover:scale-[1.02] active:scale-95"
                data-testid="onboarding-next"
              >
                Continue <ArrowRight size={14} />
              </button>
            )}
            {step === 1 && (
              <button
                onClick={saveProfileAndAdvance}
                disabled={!canNext || savingProfile}
                className="rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-bold px-6 py-2.5 min-h-[48px] text-sm disabled:opacity-40 inline-flex items-center gap-2 transition-transform hover:scale-[1.02] active:scale-95"
                data-testid="onboarding-next"
              >
                {savingProfile ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <>Continue <ArrowRight size={14} /></>}
              </button>
            )}
            {step === 2 && (
              <button
                onClick={() => setStep(3)}
                data-testid="onboarding-next"
                className="rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-bold px-6 py-2.5 min-h-[48px] text-sm inline-flex items-center gap-2 transition-transform hover:scale-[1.02] active:scale-95"
              >
                Continue <ArrowRight size={14} />
              </button>
            )}
            {step === 3 && !uploadedName && (
              <>
                <button
                  onClick={finish}
                  className="rounded-full border border-white/15 hover:border-white/30 text-white/80 px-4 py-2.5 min-h-[44px] text-sm inline-flex items-center gap-1.5"
                  data-testid="onboarding-skip-upload"
                >
                  <SkipForward size={13} /> Skip for now
                </button>
                <button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploading}
                  className="rounded-full bg-dox-orange hover:bg-dox-orangeHover text-white font-bold px-6 py-2.5 min-h-[48px] text-sm inline-flex items-center gap-2 disabled:opacity-40"
                  data-testid="onboarding-choose-file"
                >
                  <Upload size={14} /> Choose a document
                </button>
              </>
            )}
            {step === 3 && uploadedName && (
              <button
                onClick={finish}
                className="rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-black px-6 py-2.5 min-h-[48px] text-sm inline-flex items-center gap-2 shadow-lg shadow-emerald-500/25"
                data-testid="onboarding-finish"
              >
                <Sparkles size={14} /> Let Dox do it
              </button>
            )}
          </div>
        </div>
      </div>
      )}
    </div>
  );
}

function StepDot({ on, label, muted }) {
  return (
    <div className="flex items-center gap-2 shrink-0">
      <span className={`h-2 w-2 rounded-full ${on ? "bg-dox-orange" : "bg-white/20"}`} />
      <span className={on ? "text-white/80 font-semibold" : ""}>
        {label}{muted && !on ? <span className="text-white/30 ml-1">(optional)</span> : null}
      </span>
    </div>
  );
}

/**
 * EditableChip — tap to correct a single AI-extracted value before Dox files it.
 * On save, PATCHes /api/documents/{docId}/extracted-fields with only the delta.
 */
function EditableChip({ fieldKey, label, value, docId, onSaved }) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(value ?? "");
  const [saving, setSaving] = React.useState(false);
  const inputRef = React.useRef(null);

  React.useEffect(() => {
    if (editing) setTimeout(() => inputRef.current?.select(), 0);
  }, [editing]);

  const save = async () => {
    const next = String(draft || "").trim();
    if (next === (value || "").trim()) { setEditing(false); return; }
    if (!docId) { setEditing(false); return; }
    setSaving(true);
    try {
      await api.patch(`/documents/${docId}/extracted-fields`, { extracted_fields: { [fieldKey]: next } });
      onSaved && onSaved(next);
      setEditing(false);
      toast.success(`Updated ${label.toLowerCase()}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save that correction.");
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setDraft(value ?? ""); setEditing(false); };

  return (
    <div
      className={`rounded-lg bg-white/5 border ${editing ? "border-dox-orange/60" : "border-white/10 hover:border-white/25"} px-2.5 py-1.5 transition-colors`}
      data-testid={`extracted-${fieldKey}`}
    >
      <div className="text-[10px] uppercase tracking-wider text-white/45 flex items-center justify-between">
        <span>{label}</span>
        {!editing && (
          <button
            onClick={() => setEditing(true)}
            className="text-[10px] text-white/40 hover:text-dox-orange underline decoration-dotted underline-offset-2"
            data-testid={`extracted-${fieldKey}-edit`}
          >
            Edit
          </button>
        )}
      </div>
      {editing ? (
        <div className="mt-1 flex items-center gap-1.5">
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") save(); if (e.key === "Escape") cancel(); }}
            className="flex-1 min-w-0 bg-white/10 border border-white/20 rounded px-2 py-1 text-xs text-white outline-none focus:border-dox-orange"
            data-testid={`extracted-${fieldKey}-input`}
            disabled={saving}
          />
          <button
            onClick={save}
            disabled={saving}
            className="text-[10px] font-bold text-emerald-300 hover:text-emerald-200 disabled:opacity-50 px-1.5 py-1"
            data-testid={`extracted-${fieldKey}-save`}
            aria-label="Save"
          >
            {saving ? "…" : "Save"}
          </button>
          <button
            onClick={cancel}
            disabled={saving}
            className="text-[10px] text-white/40 hover:text-white/70 px-1.5 py-1"
            data-testid={`extracted-${fieldKey}-cancel`}
            aria-label="Cancel"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="mt-0.5 text-xs font-semibold text-white truncate w-full text-left"
          data-testid={`extracted-${fieldKey}-value`}
        >
          {value || <span className="text-white/40 italic">not set</span>}
        </button>
      )}
    </div>
  );
}


/**
 * CompletionCard — the "Setup complete — Dox is running for you." celebration
 * shown right after the user finishes the onboarding wizard.
 */
function CompletionCard({ firstUploadName, hasStates, hasTrades, onGo }) {
  return (
    <div
      className="relative w-full max-w-lg rounded-3xl glass-strong border border-emerald-400/25 overflow-hidden shadow-2xl shadow-emerald-500/10"
      data-testid="onboarding-completion"
    >
      {/* Ambient glow */}
      <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-emerald-500/25 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -left-16 h-60 w-60 rounded-full bg-dox-blue/20 blur-3xl pointer-events-none" />

      <div className="relative px-7 sm:px-9 pt-9 sm:pt-11 pb-7 sm:pb-9 text-center">
        <div className="mx-auto h-16 w-16 rounded-2xl bg-emerald-500/15 border border-emerald-400/40 flex items-center justify-center mb-5">
          <Check size={30} className="text-emerald-300" />
        </div>
        <div className="text-[11px] font-bold tracking-[0.24em] uppercase text-emerald-300 mb-2">
          You&apos;re all set
        </div>
        <h2 className="font-display font-black text-2xl sm:text-3xl leading-tight text-white">
          Setup complete — Dox is running for you.
        </h2>
        <p className="text-white/70 text-sm mt-3 leading-relaxed max-w-md mx-auto">
          I&apos;ll monitor every renewal, prepare each filing, and only interrupt you when a signature or
          payment is genuinely needed. You can walk away.
        </p>

        <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2.5 text-left">
          <SummaryTile label="States watched" value={hasStates} testid="completion-states" />
          <SummaryTile label="Trades covered" value={hasTrades} testid="completion-trades" />
          <SummaryTile
            label="First document"
            value={firstUploadName ? "On my desk" : "Skipped"}
            small
            testid="completion-first-doc"
          />
        </div>

        <button
          onClick={onGo}
          data-testid="completion-go-btn"
          className="mt-7 w-full rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-black px-6 py-3 min-h-[52px] text-sm inline-flex items-center justify-center gap-2 shadow-lg shadow-emerald-500/25 transition-transform hover:scale-[1.01] active:scale-95"
        >
          <Sparkles size={15} /> Take me to my Command Center
        </button>
        <div className="text-[11px] text-white/45 mt-3">
          You can adjust states, trades, and documents anytime from the Roadmap.
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, small, testid }) {
  return (
    <div className="rounded-xl bg-white/[0.04] border border-white/10 px-3 py-2.5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider text-white/45">{label}</div>
      <div className={`font-display font-black text-white ${small ? "text-sm mt-1" : "text-xl"}`}>
        {value || 0}
      </div>
    </div>
  );
}
